import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Menu, X, PenSquare, LogIn, LogOut, User } from 'lucide-react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { cn } from '@utils/cn';
import { currentAppUserSelector } from 'sdk/modules/user';
import { getInstance } from 'sdk';
import { useTheme } from '@hooks/index';
import Container from '../Container';
import Button from '../Button';
import Avatar from '../Avatar';
import ThemeToggle from '../ThemeToggle';
import { APP_ROUTES } from '@routes/consts';
import { toast } from 'react-toastify';

const { loginApi: { logout }, userApi: { loadCurrentUser } } = getInstance();

export function Navbar() {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const { isDark } = useTheme();
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
    const currentUser = useSelector(currentAppUserSelector);

    useEffect(() => {
        loadCurrentUser();
    }, []);

    useEffect(() => {
        const handleScroll = () => setIsScrolled(window.scrollY > 20);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        setIsMobileMenuOpen(false);
        setIsUserMenuOpen(false);
    }, [location.pathname]);

    const handleLogout = async () => {
        try {
            await logout();
            toast.success(t('logout.success'));
            navigate('/');
        } catch {
            toast.error(t('logout.error'));
        }
    };

    const navLinks = [
        { path: '/', label: t('nav.home') },
        { path: 'https://appna.io', label: t('nav.website'), external: true },
        { path: 'https://docs.appna.io', label: t('nav.docs'), external: true },
    ];

    return (
        <motion.header
            initial={{ y: -100 }}
            animate={{ y: 0 }}
            className={cn(
                'fixed top-0 left-0 right-0 z-50 transition-all duration-500',
                isScrolled ? 'py-2' : 'py-4'
            )}
        >
            <Container>
                <nav className={cn(
                    'relative flex items-center justify-between rounded-2xl px-6 transition-all duration-500',
                    isScrolled
                        ? cn(
                            'h-14 backdrop-blur-xl shadow-lg border',
                            isDark 
                                ? 'bg-dark-50/80 shadow-black/20 border-primary-500/10' 
                                : 'bg-white/80 shadow-gray-200/50 border-gray-200/50'
                        )
                        : 'h-16 bg-transparent'
                )}>
                    {/* Logo */}
                    <Link to="/" className="flex items-center gap-2.5 group z-10">
                        <img
                            src="/ico.svg"
                            alt="AppNA Blog"
                            className="w-8 h-8 transition-transform group-hover:scale-105"
                        />
                        <div className="flex flex-col">
                            <span className={cn(
                                'text-lg font-semibold leading-tight',
                                isDark ? 'text-white' : 'text-gray-900'
                            )}>
                                AppNA
                            </span>
                            <span className={cn(
                                'text-[10px] font-medium tracking-wide',
                                isDark ? 'text-dark-400' : 'text-gray-500'
                            )}>
                                Blog
                            </span>
                        </div>
                    </Link>

                    {/* Desktop Navigation */}
                    <div className="hidden md:flex items-center gap-1 absolute left-1/2 -translate-x-1/2">
                        {navLinks.map((link) => (
                            link.external ? (
                                <a
                                    key={link.path}
                                    href={link.path}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={cn(
                                        'px-4 py-2 rounded-lg text-sm font-medium transition-all',
                                        isDark 
                                            ? 'text-dark-500 hover:text-white hover:bg-white/5' 
                                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                                    )}
                                >
                                    {link.label}
                                </a>
                            ) : (
                                <Link
                                    key={link.path}
                                    to={link.path}
                                    className={cn(
                                        'px-4 py-2 rounded-lg text-sm font-medium transition-all',
                                        location.pathname === link.path
                                            ? isDark ? 'text-white bg-white/10' : 'text-gray-900 bg-gray-100'
                                            : isDark ? 'text-dark-500 hover:text-white hover:bg-white/5' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                                    )}
                                >
                                    {link.label}
                                </Link>
                            )
                        ))}
                    </div>

                    {/* Right Side */}
                    <div className="hidden md:flex items-center gap-3 z-10">
                        <ThemeToggle />
                        {currentUser ? (
                            <>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => navigate(`/${APP_ROUTES.BLOGGING}`)}
                                    leftIcon={<PenSquare size={16} />}
                                >
                                    {t('nav.write')}
                                </Button>
                                <div className="relative">
                                    <button
                                        onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                                        className={cn(
                                            'flex items-center gap-2 p-1 rounded-lg transition-colors',
                                            isDark ? 'hover:bg-white/5' : 'hover:bg-gray-100'
                                        )}
                                    >
                                        <Avatar
                                            src={currentUser?.displayInfo?.avatar}
                                            alt={currentUser?.displayInfo?.fullName}
                                            size="sm"
                                        />
                                    </button>
                                        {isUserMenuOpen && (
                                        <div className={cn(
                                            'absolute right-0 mt-2 w-48 py-2 rounded-xl border shadow-xl',
                                            isDark ? 'bg-dark-50 border-dark-200' : 'bg-white border-gray-200'
                                        )}>
                                            <div className={cn(
                                                'px-4 py-2 border-b',
                                                isDark ? 'border-dark-200' : 'border-gray-200'
                                            )}>
                                                <p className={cn(
                                                    'text-sm font-medium truncate',
                                                    isDark ? 'text-white' : 'text-gray-900'
                                                )}>
                                                    {currentUser?.displayInfo?.fullName}
                                                </p>
                                                <p className={cn(
                                                    'text-xs truncate',
                                                    isDark ? 'text-dark-500' : 'text-gray-500'
                                                )}>
                                                    {currentUser?.displayInfo?.email}
                                                </p>
                                            </div>
                                            <Link
                                                to={`/${APP_ROUTES.PROFILE}`}
                                                className={cn(
                                                    'w-full flex items-center gap-2 px-4 py-2 text-sm transition-colors',
                                                    isDark 
                                                        ? 'text-dark-400 hover:text-white hover:bg-white/5' 
                                                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                                                )}
                                            >
                                                <User size={16} />
                                                {t('nav.profile')}
                                            </Link>
                                            <button
                                                onClick={handleLogout}
                                                className={cn(
                                                    'w-full flex items-center gap-2 px-4 py-2 text-sm transition-colors',
                                                    isDark 
                                                        ? 'text-dark-400 hover:text-white hover:bg-white/5' 
                                                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                                                )}
                                            >
                                                <LogOut size={16} />
                                                {t('nav.logout')}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </>
                        ) : (
                            <Button
                                variant="gradient"
                                size="sm"
                                onClick={() => navigate(`/${APP_ROUTES.LOGIN}`)}
                                leftIcon={<LogIn size={16} />}
                                glow
                            >
                                {t('nav.login')}
                            </Button>
                        )}
                    </div>

                    {/* Mobile Right Side */}
                    <div className="flex md:hidden items-center gap-2">
                        <ThemeToggle />
                        <button
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                            className={cn(
                                'w-10 h-10 rounded-lg flex items-center justify-center transition-colors',
                                isDark 
                                    ? 'text-dark-400 hover:text-white hover:bg-white/5' 
                                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                            )}
                        >
                            {isMobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
                        </button>
                    </div>
                </nav>
            </Container>

            {/* Mobile Menu */}
            {isMobileMenuOpen && (
                <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.2 }}
                    className="md:hidden overflow-hidden"
                >
                    <Container className="py-4">
                        <div className={cn(
                            'backdrop-blur-xl rounded-2xl border p-4',
                            isDark ? 'bg-surface/95 border-dark-200' : 'bg-white/95 border-gray-200'
                        )}>
                            <div className="flex flex-col gap-1">
                                {navLinks.map((link) => (
                                    link.external ? (
                                        <a
                                            key={link.path}
                                            href={link.path}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={cn(
                                                'px-4 py-3 rounded-xl text-sm font-medium transition-colors',
                                                isDark 
                                                    ? 'text-dark-500 hover:text-white hover:bg-white/5' 
                                                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                                            )}
                                        >
                                            {link.label}
                                        </a>
                                    ) : (
                                        <Link
                                            key={link.path}
                                            to={link.path}
                                            className={cn(
                                                'px-4 py-3 rounded-xl text-sm font-medium transition-colors',
                                                location.pathname === link.path
                                                    ? isDark ? 'text-white bg-white/10' : 'text-gray-900 bg-gray-100'
                                                    : isDark ? 'text-dark-500 hover:text-white hover:bg-white/5' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                                            )}
                                        >
                                            {link.label}
                                        </Link>
                                    )
                                ))}

                                <div className={cn(
                                    'pt-4 mt-4 border-t',
                                    isDark ? 'border-dark-200' : 'border-gray-200'
                                )}>
                                    {currentUser ? (
                                        <div className="space-y-2">
                                            <div className="flex items-center gap-3 px-4 py-2">
                                                <Avatar
                                                    src={currentUser?.displayInfo?.avatar}
                                                    alt={currentUser?.displayInfo?.fullName}
                                                    size="md"
                                                />
                                                <div>
                                                    <p className={cn(
                                                        'text-sm font-medium',
                                                        isDark ? 'text-white' : 'text-gray-900'
                                                    )}>
                                                        {currentUser?.displayInfo?.fullName}
                                                    </p>
                                                    <p className={cn(
                                                        'text-xs',
                                                        isDark ? 'text-dark-500' : 'text-gray-500'
                                                    )}>
                                                        {currentUser?.displayInfo?.email}
                                                    </p>
                                                </div>
                                            </div>
                                            <Button
                                                fullWidth
                                                variant="secondary"
                                                onClick={() => navigate(`/${APP_ROUTES.BLOGGING}`)}
                                                leftIcon={<PenSquare size={16} />}
                                            >
                                                {t('nav.write')}
                                            </Button>
                                            <Button
                                                fullWidth
                                                variant="ghost"
                                                onClick={() => navigate(`/${APP_ROUTES.PROFILE}`)}
                                                leftIcon={<User size={16} />}
                                            >
                                                {t('nav.profile')}
                                            </Button>
                                            <Button
                                                fullWidth
                                                variant="ghost"
                                                onClick={handleLogout}
                                                leftIcon={<LogOut size={16} />}
                                            >
                                                {t('nav.logout')}
                                            </Button>
                                        </div>
                                    ) : (
                                        <Button
                                            fullWidth
                                            variant="gradient"
                                            onClick={() => navigate(`/${APP_ROUTES.LOGIN}`)}
                                            leftIcon={<User size={16} />}
                                        >
                                            {t('nav.login')}
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </Container>
                </motion.div>
            )}
        </motion.header>
    );
}

export default Navbar;
